import {Injectable} from '@angular/core';
import {Actions, createEffect, CreateEffectMetadata, ofType} from '@ngrx/effects';
import {AppState} from '../store';
import {Store} from '@ngrx/store';
import {DimensionsService} from '../../services/dimensions.service';
import {Observable, switchMap, withLatestFrom} from 'rxjs';
import {TypedAction} from '@ngrx/store/src/models';
import {
  createDataSetConditionalDimension,
  createDataSetConditionalDimensionSuccess,
  deleteDataSetConditionalDimension,
  deleteDataSetConditionalDimensionSuccess,
  initializeDataSetConditionalDimension,
  initializeDataSetConditionalDimensionSuccess,
  loadDataSetConditionalDimensions,
  loadDataSetConditionalDimensionsSuccess,
  loadPublicDataSetConditionalDimensions,
  loadPublicDataSetConditionalDimensionsSuccess,
  updateDataSetConditionalDimension,
  updateDataSetConditionalDimensions,
  updateDataSetConditionalDimensionsSuccess,
  updateDataSetConditionalDimensionSuccess
} from './dataSetConditionalDimensions.actions';
import {filter, map} from 'rxjs/operators';
import {selectSite} from '../init/init.selectors';
import {Serializer, SerializerResponse, Serializers} from '../../interfaces/serializer';
import {DataSetConditionalDimension} from '../../interfaces/dimensions';
import {Site} from '../../interfaces/site';
import {
  loadDataSetDimensions,
  loadDataSetDimensionsSuccess,
  updateDataSetDimensions,
  updateDataSetDimensionsSuccess
} from '../dataSetDimensions/dataSetDimensions.actions';
import {selectDataSetDimensionsState} from '../dataSetDimensions/dataSetDimensions.selectors';
import {DimensionComponentObjects} from '../../classes/dimension-component-object';
import {loadDataSourceDimensions, loadDataSourceDimensionsSuccess} from '../dataSourceDimensions/dataSourceDimensions.actions';
import {selectDataSourceDimensionsState} from '../dataSourceDimensions/dataSourceDimensions.selectors';
import {DataSetDimensionsState} from '../dataSetDimensions/dataSetDimensions';
import {DataSourceDimensionsState} from '../dataSourceDimensions/dataSourceDimensions';
import {selectDataSetConditionalDimensionsState} from './dataSetConditionalDimensions.selectors';
import {DataSetConditionalDimensionsState} from './dataSetConditionalDimensions';
import {PublicReportsService} from '../../services/public-reports.service';
import {selectGeneralDimensions, selectGeneralDimensionsState} from '../generalDimensions/generalDimensions.selectors';
import {GeneralDimensionsState} from '../generalDimensions/generalDimensions';

@Injectable()
export class DataSetConditionalDimensionsEffects {
  private readonly _dataset$: Observable<Serializer<Site>> = this._store.select(selectSite);

  private readonly _load$: Observable<TypedAction<string> & { dimensions: Serializers<DataSetConditionalDimension> }> = createEffect((): Observable<TypedAction<string> & { dimensions: Serializers<DataSetConditionalDimension> }> => this._actions$.pipe(
    ofType(loadDataSetConditionalDimensions),
    withLatestFrom(this._dataset$),
    switchMap(([_, dataset]: [TypedAction<string>, Serializer<Site>]): Observable<SerializerResponse<Serializers<DataSetConditionalDimension>>> => this._dimensionsService.getDataSetConditionalDimensions(dataset)),
    map((response: SerializerResponse<Serializers<DataSetConditionalDimension>>): TypedAction<string> & { dimensions: Serializers<DataSetConditionalDimension> } => loadDataSetConditionalDimensionsSuccess({
      dimensions: response.data
    }))
  ));

  private readonly _loadPublic$: Observable<TypedAction<string> & { dimensions: Serializers<DataSetConditionalDimension> }> = createEffect((): Observable<TypedAction<string> & { dimensions: Serializers<DataSetConditionalDimension> }> => this._actions$.pipe(
    ofType(loadPublicDataSetConditionalDimensions),
    switchMap((action: TypedAction<string> & { uuid: string, token: string }): Observable<SerializerResponse<Serializers<DataSetConditionalDimension>>> => this._publicReportsService.getDataSetConditionalDimensions(action.uuid, action.token)),
    map((response: SerializerResponse<Serializers<DataSetConditionalDimension>>): TypedAction<string> & { dimensions: Serializers<DataSetConditionalDimension> } => loadPublicDataSetConditionalDimensionsSuccess({
      dimensions: response.data
    }))
  ));

  private readonly _updates$: Observable<TypedAction<string> & { dimensions: Serializers<DataSetConditionalDimension> }> = createEffect((): Observable<TypedAction<string> & { dimensions: Serializers<DataSetConditionalDimension> }> => this._actions$.pipe(
    ofType(updateDataSetConditionalDimensions),
    withLatestFrom(this._dataset$),
    switchMap(([_, dataset]: [TypedAction<string>, Serializer<Site>]): Observable<SerializerResponse<Serializers<DataSetConditionalDimension>>> => this._dimensionsService.getDataSetConditionalDimensions(dataset)),
    map((response: SerializerResponse<Serializers<DataSetConditionalDimension>>): TypedAction<string> & { dimensions: Serializers<DataSetConditionalDimension> } => updateDataSetConditionalDimensionsSuccess({
      dimensions: response.data
    }))
  ));


  private readonly _create$: Observable<TypedAction<string> & { dimension: Serializer<DataSetConditionalDimension> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { dimension: Serializer<DataSetConditionalDimension> }> => this._actions$.pipe(
    ofType(createDataSetConditionalDimension),
    withLatestFrom(this._dataset$),
    switchMap(([action, dataset]: [TypedAction<string> & { dimension: Partial<DataSetConditionalDimension> }, Serializer<Site>]): Observable<SerializerResponse<Serializer<DataSetConditionalDimension>>> => this._dimensionsService.createDataSetConditionalDimension(dataset, action.dimension)),
    map((response: SerializerResponse<Serializer<DataSetConditionalDimension>>): TypedAction<string> & { dimension: Serializer<DataSetConditionalDimension> } => createDataSetConditionalDimensionSuccess({
      dimension: response.data
    }))
  ));

  private readonly _update$: Observable<TypedAction<string> & { dimension: Serializer<DataSetConditionalDimension> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { dimension: Serializer<DataSetConditionalDimension> }> => this._actions$.pipe(
    ofType(updateDataSetConditionalDimension),
    withLatestFrom(this._dataset$),
    switchMap(([action, dataset]: [TypedAction<string> & { dimension: Serializer<DataSetConditionalDimension>, update: Partial<DataSetConditionalDimension> }, Serializer<Site>]): Observable<SerializerResponse<Serializer<DataSetConditionalDimension>>> => this._dimensionsService.updateDataSetConditionalDimension(dataset, action.dimension, action.update)),
    map((response: SerializerResponse<Serializer<DataSetConditionalDimension>>): TypedAction<string> & { dimension: Serializer<DataSetConditionalDimension> } => updateDataSetConditionalDimensionSuccess({
      dimension: response.data
    }))
  ));

  private readonly _delete$: Observable<TypedAction<string> & { dimension: Serializer<DataSetConditionalDimension> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { dimension: Serializer<DataSetConditionalDimension> }> => this._actions$.pipe(
    ofType(deleteDataSetConditionalDimension),
    withLatestFrom(this._dataset$),
    switchMap(([action, dataset]: [TypedAction<string> & { dimension: Serializer<DataSetConditionalDimension> }, Serializer<Site>]): Observable<SerializerResponse<Serializer<DataSetConditionalDimension>>> => this._dimensionsService.deleteDataSetConditionalDimension(dataset, action.dimension)),
    map((response: SerializerResponse<Serializer<DataSetConditionalDimension>>): TypedAction<string> & { dimension: Serializer<DataSetConditionalDimension> } => deleteDataSetConditionalDimensionSuccess({
      dimension: response.data
    }))
  ));

  // ##########
  // ## INIT ##
  // ##########

  private readonly _init$: Observable<any> = createEffect((): Observable<TypedAction<string>> => this._actions$.pipe(
    ofType(
      loadDataSetDimensions,
      loadDataSourceDimensions,
      updateDataSetDimensions,
      updateDataSetConditionalDimensions,
      updateDataSetConditionalDimension,
      createDataSetConditionalDimension
    ),
    map((): TypedAction<string> => initializeDataSetConditionalDimension())
  ));

  private readonly _initSuccess$: Observable<any> = createEffect((): Observable<TypedAction<string> & { dimensions: DimensionComponentObjects }> => this._actions$.pipe(
    ofType(
      loadDataSetConditionalDimensionsSuccess,
      loadDataSetDimensionsSuccess,
      loadDataSourceDimensionsSuccess,
      updateDataSetDimensionsSuccess,
      updateDataSetConditionalDimensionsSuccess,
      updateDataSetConditionalDimensionSuccess,
      createDataSetConditionalDimensionSuccess
    ),
    withLatestFrom(
      this._store.select(selectDataSetConditionalDimensionsState),
      this._store.select(selectDataSetDimensionsState),
      this._store.select(selectDataSourceDimensionsState),
      this._store.select(selectGeneralDimensionsState)
    ),
    filter(([_, conditionalDimensionsState, dimensionsState, datasourceDimensionsState, generalDimensionsState]: [TypedAction<string>, DataSetConditionalDimensionsState, DataSetDimensionsState, DataSourceDimensionsState, GeneralDimensionsState]): boolean => conditionalDimensionsState.loaded && dimensionsState.loaded && datasourceDimensionsState.loaded),
    map(([_, conditionalDimensionsState, dimensions, datasourceDimensions, generalDimensionsState]: [TypedAction<string>, DataSetConditionalDimensionsState, DataSetDimensionsState, DataSourceDimensionsState, GeneralDimensionsState]): TypedAction<string> & { dimensions: DimensionComponentObjects } => initializeDataSetConditionalDimensionSuccess({
      dimensions: dimensions.componentObjects.all,
      datasourceDimensions: datasourceDimensions.componentObjects,
      generalDimensions: generalDimensionsState.componentObjects
    }))
  ));

  constructor(
    private readonly _actions$: Actions,
    private readonly _dimensionsService: DimensionsService,
    private readonly _store: Store<AppState>,
    private readonly _publicReportsService: PublicReportsService
  ) {}

}
